import React from "react";

import { Box } from "@material-ui/core";
import { MapRef } from "react-map-gl";

import MapBase from "components/visuals/maps/MapBase";
import SelectedStoreMarker from "components/visuals/maps/SelectedStoreMarker";

import CoreRetailHub from "./CoreRetailHub";
import Legend from "./Legend";
import Marker from "./Marker";

import { useAppSelector } from "store";
import { selectMapboxConfiguration } from "modules/siteConfiguration/siteConfigurationSlice";
import { selectStore } from "modules/customer/insights/portfolioNew/portfolioSlice";
import {
    selectSelectedStoreCompetitorsWithin5Kms,
    selectSelectedStoreCoreRetailHub,
    selectIsLoading,
    selectHasErrors
} from "modules/customer/insights/portfolioNew/competition/competitionSlice";
import { Competitor } from "modules/customer/insights/portfolioNew/competition/competitors";

const Map: React.FC = () => {
    const mapRef = React.useRef<MapRef>(null);
    const mapboxConfiguration = useAppSelector(selectMapboxConfiguration);
    const selectedStore = useAppSelector(selectStore);
    const selectedStoreCoreRetailHub = useAppSelector(selectSelectedStoreCoreRetailHub);
    const competitors = useAppSelector(selectSelectedStoreCompetitorsWithin5Kms);
    const isLoading = useAppSelector(selectIsLoading);
    const hasErrors = useAppSelector(selectHasErrors);
    
    const [hoveredCompetitor, setHoveredCompetitor] = React.useState<Competitor | undefined>(undefined);
    const [initialViewport, setInitialViewport] = React.useState({
        latitude: selectedStore?.latitude ?? 0,
        longitude: selectedStore?.longitude ?? 0,
        zoom: 13.5,
        minZoom: 4
    });

    React.useEffect(() => {
        setInitialViewport(current => {
            return {
                ...current,
                latitude: selectedStore?.latitude ?? 0,
                longitude: selectedStore?.longitude ?? 0
            };
        });
    }, [selectedStore]);

    return (
        <Box data-cy="competition-map">
            <MapBase
                loading={isLoading}
                error={hasErrors}
                title={`Competitors in the surrounding area of your ${selectedStore?.name} store`}
                mapboxAccessToken={mapboxConfiguration.accessToken}
                mapboxBaseMapStyle={mapboxConfiguration.baseMapStyle}
                initialViewport={initialViewport}
                addGeocoder={false}
                addNavigationControl={true}
                addRecenterButton={true}
                dataCy="competitors-map"
                mapRef={mapRef}
                downloadData={competitors}
                addFullscreenButton={true}
            >
                <SelectedStoreMarker latitude={selectedStore?.latitude ?? 0} longitude={selectedStore?.longitude ?? 0} />
                {competitors?.map((competitor, index) =>
                    <Marker
                        key={index}
                        competitor={competitor}
                        onTooltipOpen={() => setHoveredCompetitor(competitor)}
                        onTooltipClose={() => setHoveredCompetitor(undefined)}
                    />
                )}
                <CoreRetailHub />
                <Legend store={selectedStore} competitor={hoveredCompetitor} coreRetailHub={selectedStoreCoreRetailHub} />
            </MapBase>
        </Box>
    );
};

export default Map;
